import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RTL_LANGUAGES } from './i18n';
import storage from '@/utils/storage';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SiteConfigProvider } from './hooks/useSiteConfig.jsx';
import { GameProvider } from './Context/GameContext';
import { BettingProvider } from './Context/BettingContext';
import { ExposureProvider } from './hooks/useExposure';
import Layout from './components/layout/Layout';
import Splash from './pages/Splash/Splash';
import Home from './pages/Home/Home';
import { useGamePrefetch } from './hooks/useGamePrefetch';

// Lazy-loaded pages (code-split into separate chunks)
const Game = lazy(() => import('./pages/Game/Game'));
const Settings = lazy(() => import('./pages/Settings/Settings'));
const ProvablyFair = lazy(() => import('./pages/ProvablyFair/ProvablyFair'));
const Sports = lazy(() => import('./pages/Sports/Sports'));
const MatchDetail = lazy(() => import('./pages/Sports/components/MatchDetail/MatchDetail'));
const Menu = lazy(() => import('./pages/Menu/Menu.jsx'));
const ProfileMenuMobile = lazy(() => import('./components/common/ProfileMenu/ProfileMenuMobile'));
const Promotions = lazy(() => import('./pages/Promotions/Promotions'));
const Affiliate = lazy(() => import('./pages/Affiliate/Affiliate'));
const VipClub = lazy(() => import('./pages/VipClub/VipClub'));
const Bonus = lazy(() => import('./pages/Bonus/Bonus'));
const Casino = lazy(() => import('./pages/Casino/Casino'));
const Support = lazy(() => import('./pages/Support/Support'));
const GiftCards = lazy(() => import('./pages/GiftCards/GiftCards'));
const Trade = lazy(() => import('./pages/Trade/Trade'));
const TradeOrderDetail = lazy(() => import('./pages/Trade/components/TradeOrderDetail/TradeOrderDetail'));
const SellOrderDetail = lazy(() => import('./pages/Trade/components/SellOrderDetail/SellOrderDetail'));
const TradeOrdersPage = lazy(() => import('./pages/Trade/components/TradeOrdersPage/TradeOrdersPage'));
const DisputePage = lazy(() => import('./pages/Trade/components/DisputePage/DisputePage'));
const CryptMet = lazy(() => import('./pages/CryptMet/CryptMet'));
const Install = lazy(() => import('./pages/Install/Install'));
const SportsBet = lazy(() => import('./pages/SportsBet/SportsBet'));
const ReferralRedirect = lazy(() => import('./pages/ReferralRedirect/ReferralRedirect'));

// Lazy-loaded modals (not needed on initial render)
const AuthModal = lazy(() => import('./components/auth/AuthModal'));
const WalletModal = lazy(() => import('./components/wallet/WalletModal'));
const GlobalSearchModal = lazy(() => import('./components/common/GlobalSearchModal/GlobalSearchModal'));
const ProfileModal = lazy(() => import('./components/profile/ProfileModal'));
const BetHistoryModal = lazy(() => import('./components/betHistory/BetHistoryModal'));
const SettingsModal = lazy(() => import('./components/settings/SettingsModal'));
const NotificationModal = lazy(() => import('./components/notifications/NotificationModal'));

/**
 * Inner App Component - Has access to QueryClient
 */
function AppContent() {
  const { i18n } = useTranslation();
  const [showSplash, setShowSplash] = useState(true);
  const [searchParams] = useSearchParams();

  // Capture affiliate/referral code from URL params and persist to localStorage
  useEffect(() => {
    const ref = searchParams.get('ref') || searchParams.get('aff');
    if (ref && ref.trim()) {
      storage.setKey('aff', ref.trim());
    }
  }, [searchParams]);

  const handleSplashComplete = () => {
    // Hide splash when animation completes
    setShowSplash(false);
  };

  // Pre-fetch games in background for smooth search experience
  useGamePrefetch();

  return (
    <>
      {/* Splash Screen - Shows on every page refresh */}
      {showSplash && <Splash onComplete={handleSplashComplete} />}

            <Suspense fallback={null}>
            <Routes>
              {/* Referral routes - top-level, outside Layout to prevent conflict with /:provider/:gameid catch-all */}
              <Route path="referal/:code" element={<ReferralRedirect />} />
              <Route path="referral/:code" element={<ReferralRedirect />} />

              {/* Main Layout Routes */}
              <Route path="/" element={<Layout />}>
                <Route index element={<Home />} />
                {/* Login route - shows home page with modal via query params */}
                <Route path="login" element={<Home />} />
                {/* Wallet route - opens wallet modal with query params: /wallet?mode=deposit|withdraw|swap */}
                <Route path="wallet" element={<Home />} />
                {/* Modal route - opens modals via query params */}
                <Route path="modal" element={<Home />} />
                <Route path="notifications" element={<Navigate to="/modal?mode=notification" replace />} />
                <Route path="menu" element={<Menu />} />
                <Route path="profile-menu" element={<ProfileMenuMobile />} />
                {/* Settings route - full page */}
                <Route path="settings" element={<Settings />} />
                <Route path="global-settings" element={<Settings />} />
                {/* Provably Fair / Help Center route */}
                <Route path="provably-fair" element={<ProvablyFair />} />
                {/* Promotions route */}
                <Route path="promotions" element={<Promotions />} />
                {/* Affiliate routes - must be before catch-all game route */}
                <Route path="affiliate/*" element={<Affiliate />} />
                {/* VIP Club route */}
                <Route path="vip-club" element={<VipClub />} />
                {/* Redeem & Rakeback route */}
                <Route path="bonus-page" element={<Bonus />} />
                {/* Casino routes */}
                <Route path="casino" element={<Casino />} />
                <Route path="casino/provider/:providerName" element={<Casino />} />
                <Route path="casino/:category" element={<Casino />} />
                {/* Support route */}
                <Route path="support" element={<Support />} />
                {/* Gift Cards route */}
                <Route path="gift-cards" element={<GiftCards />} />
                {/* P2P Trade marketplace */}
                <Route path="trade" element={<Trade />} />
                <Route path="trade/orders" element={<TradeOrdersPage />} />
                <Route path="trade/order/:orderId" element={<TradeOrderDetail />} />
                <Route path="trade/order/:orderId/dispute" element={<DisputePage />} />
                <Route path="trade/sell-order/:orderId" element={<SellOrderDetail />} />
                <Route path="trade/sell-order/:orderId/dispute" element={<DisputePage />} />
                {/* CRYPTMET Exchange (desk-model INR <-> USDT) */}
                <Route path="cryptmet" element={<CryptMet />} />
                <Route path="cryptmet/order/:orderId" element={<CryptMet />} />
                {/* Sports Bet (Slotegrator/BetBy) */}
                <Route path="sports-bet" element={<SportsBet />} />
                {/* Install (PWA) app page */}
                <Route path="install" element={<Install />} />
                {/* 
                  Game route - /providername/gameid
                  
                  This route handles all game launches:
                  - GIS games: /spribe/abc123, /pragmaticplay/xyz789, etc.
                  - JS games (E-sports): /esports/4ee8e0051a035b463b47c3c473ce317d
                  
                  Flow:
                  1. User clicks game → handleGameLaunch() or handleJsGameLaunch() called
                  2. Game API called → gets game URL
                  3. navigate(`/${providerName}/${gameId}`) → navigates to this route
                  4. <Game /> component receives params: { providername, gameid }
                  5. Game component displays iframe with game URL
                  
                  Example URLs:
                  - /spribe/crash-game-123 → GIS game
                  - /esports/4ee8e0051a035b463b47c3c473ce317d → E-sports (JS game)
                  
                  IMPORTANT: This catch-all route must be LAST to avoid matching other routes
                */}
                <Route path=":providername/:gameid" element={<Game />} />
                {/* Sports routes - order matters: specific routes first */}
                {/* Match detail route - must be before other sports routes to avoid conflicts */}
                <Route path="sports/markets/:matchId" element={<MatchDetail />} />
                {/* Specific sports routes */}
                <Route path="sports/all-sports" element={<Sports />} />
                <Route path="sports/popular" element={<Sports />} />
                {/* Generic sports routes - must come after specific routes */}
                <Route path="sports/:tab/:sport" element={<Sports />} />
                <Route path="sports/:tab" element={<Sports />} />
                <Route path="sports" element={<Sports />} />
                {/* More routes will be added here */}
              </Route>
            </Routes>
            </Suspense>

            {/* Modals - Lazy loaded, not needed on initial render */}
            <Suspense fallback={null}>
            <AuthModal />

            {/* Wallet Modal - Rendered outside routes to overlay everything */}
            <WalletModal />

            {/* Global Search Modal - Rendered outside routes to overlay everything */}
            <GlobalSearchModal />

            {/* Profile Modal - Rendered outside routes to overlay everything */}
            <ProfileModal />

            {/* Bet History Modal - Rendered outside routes to overlay everything */}
            <BetHistoryModal />

            {/* Settings Modal - Rendered outside routes to overlay everything */}
            <SettingsModal />

      <NotificationModal />
            </Suspense>

      {/* Toast notifications for BetSlip (react-toastify) */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={RTL_LANGUAGES.includes(i18n.language)}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="dark"
        toastStyle={{
          background: 'var(--color-bg-card, #121826)',
          color: 'var(--color-text-primary, #E6EAF2)',
          border: '1px solid var(--color-border, #222B3A)',
        }}
      />
    </>
  );
}

/**
 * Main App Component
 * Sets up routing structure
 * Shows splash on every page refresh/reload as overlay
 * Wraps app with SiteConfigProvider for socket and config management
 * Wraps app with GameProvider for game search functionality
 */
function App() {
  return (
    <SiteConfigProvider>
      <ExposureProvider>
        <BettingProvider>
          <GameProvider>
            <AppContent />
          </GameProvider>
        </BettingProvider>
      </ExposureProvider>
    </SiteConfigProvider>
  );
}

export default App;
